import type { MinutesListItem, MinutesVoiceCounts } from '../../api/minutes';
import type { VoiceSessionItem } from '../../api/voice-sessions';

/**
 * 「정리 대기」를 목록에 **합치는 규칙** — 한 곳에 둔다 (2026-10-04 · F1-c).
 *
 * ## 왜 따로 떼어 놓나
 *
 * 합치는 판정이 JSX 안에 섞이면 **그리는 것을 통째로 띄워야** 잴 수 있다. 떼어 두면
 * 사보타주 한 줄이 **어느 규칙을 깼는지**를 시험 이름으로 말해 준다.
 * ⇒ ★ 그리고 이 레포의 교훈대로 — **판정은 한 곳에**(같은 뜻을 다른 글자로 두지 않는다).
 *
 * ## ⛔ 「정리 대기」 ≠ 「대기」
 *
 * 회의록의 `status: 'pending'` 라벨이 **「대기」**(`minutes.status.pending`)이고, 이 판이
 * 더하는 글자는 **「정리 대기」**다. ⇒ ★ **다른 것**이다:
 * ```
 * 「대기」      회의록 행이 «있고» AI 정리가 아직 안 끝난 것      (minutes.status === 'pending')
 * 「정리 대기」  회의록 행이 «없는» 음성 세션                      (session.minutes_id === null)
 * ```
 * ⚠ 섞어 쓰면 「내 회의록이 둘로 보인다」가 된다 — 이 레포가 겪은 「같은 낱말에 다른 뜻」 그 꼴이다.
 *
 * ## ⭐ 2026-10-08 — 그 둘이 «다른 것»인 것은 그대로지만, ★ 「같은 탭」에 선다
 *
 * 사용자 글자 그대로:
 * 「회의록이 서버에 저장이 완료되고 전체내역이 저장되면 ★ **목록에 정리 대기에 있고**
 *   해당 상세 내역에 들어가서 회의정보, 요약, 전체를 볼수있으며 …」
 * ⇒ ★ 즉 **요약이 아직 없는 회의록**도 「정리 대기」 탭에 **보여야** 한다.
 * ⇒ ⇒ ⛔ 그전에는 그 회의록이 **「전체」 탭에만** 보였다(「완료」도 「정리 대기」도 아니었다) —
 *    `status !== 'ready'` 라 ready 에서 빠지고, 「정리 대기」는 회의록을 통째로 뺐기 때문이다.
 * ⇒ ⇒ ⇒ ★ 그리고 **앱은 이미 그렇게 하고 있었다** — `feature/minutes/MinutesScreenRules.kt` 의
 *    `minutesMatchesTab` 이 `MINUTES_TAB_WAITING -> !status.equals(STATUS_READY)` 이고,
 *    그 주석이 적고 있다: 「**「정리 대기」는 대기 + 실패 둘 다 담는다(2026-09-21 사용자 결정)**」.
 *    ⇒ ⇒ ★ 그래서 **실패한 회의록도 「정리 대기」에 든다** — 그것은 이 판의 곁효과가 아니라
 *       **그 날 정해진 것**이고, 웹이 늦게 따라가는 것이다.
 *
 * ⚠ 그러니 종전 가름(「회의록 행이 «없는» 세션」)은 **틀린 것이 아니라 «좁았다»** —
 *   서버가 `pending` 을 쓰기 전에는 그것이 **「정리 안 된 것」의 대리 판정**이었다.

/**
 * 탭 — 정본(AOS)의 셋과 **같은 축**이다.
 *
 * 근거: `docs/design/aos-minutes-canon-audit-2026-10-02.md:19`(「전체 2 · 완료 0 · 정리 대기 2」)
 *      `docs/design/aos-minutes-redesign-slices-2026-09-30.md:430`
 *
 * ⛔ **`all` 이 기본이고 거기 «다» 보인다.** `AiMinutesPage.tsx` 의 `OriginBadge` 머리말이
 * 적어 둔 까닭 — 「가르면 사용자는 «내 회의록이 어디 갔나»를 묻는다」 — 가 **이 탭에도 걸린다.**
 */
export const MINUTES_TABS = ['all', 'ready', 'awaiting'] as const;
export type MinutesTab = (typeof MINUTES_TABS)[number];

/**
 * ★★ 「정리 대기」의 **판정** — `minutes_id === null` 하나다.
 *
 * ⛔ `status === 'awaiting_summary'` 로 **가르지 않는다.** 2026-10-04 로컬 실측:
 * 그 상태인 행이 **0건**이고(마이그 221 이 CHECK 만 넓히고 되메우기를 안 했다) 멈춰 있는
 * 17건이 전부 `awaiting_end` 다. ⇒ ★ 이 판정은 **옛 17건과 앞으로 생길 `awaiting_summary` 를
 * 둘 다** 잡는다.
 *
 * ## ⭐⭐ 2026-10-06 사용자 결정 — **「녹음 «중»인 것은 웹에서 조회가 안 되는 게 맞다」**
 *
 * ```
 * ⇒ status === 'collecting' 인 세션은 ⛔ 「정리 대기」에 ★ «넣지 않는다»
 * ⇒ ⚠ 그리고 ⭐ 「그럼 어디에 두나」의 답은 ★ «어디에도 두지 않는다»이다 —
 *   사용자 말이 「조회가 «안 되는» 게 맞다」이므로 ⇒ ⛔ 새 자리를 ★ 짓지 않는다.
 * 로컬 실측(2026-10-06): minutes_id NULL ⇒ 전 19 · ★ 후 17 (collecting 2 가 빠진다)
 * ```
 * ⚠ 그래도 `minutes_id === null` 은 **그대로 첫째 조건**이다 — 회의록이 생긴 세션은 여전히 빠진다.
 */
export function isAwaitingSummary(s: VoiceSessionItem): boolean {
  // ⛔ 녹음이 «도는 중»인 것은 안 보인다(2026-10-06 사용자 결정).
  if (s.status === 'collecting') return false;
  return s.minutes_id === null;
}

/**
 * 합친 목록에서 **한 줄**이 되는 것.
 *
 * ★ 둘을 **같은 타입으로 섞지 않는다** — 섞으면 「어느 쪽이 회의록인가」를 다시 판정해야 한다.
 *   `kind` 를 들고 다니면 그리는 쪽이 **묻지 않고** 고른다.
 */
export type MergedRow =
  | { kind: 'minutes'; at: string; minutes: MinutesListItem }
  | { kind: 'awaiting'; at: string; session: VoiceSessionItem };

/**
 * 정렬 — ⭐ **「정리 대기」가 ★ 먼저, 그 안에서 ★ 새것부터.**
 *
 * ## ★ 왜 날짜로 «섞지» 않나 — 한때 섞으려 했고, 그러면 두 벌이 된다
 *
 * 처음에는 「한 축(날짜)으로 섞는다」고 적었다. ⛔ 그런데 그리는 쪽은 **섞을 수가 없다**:
 * 카드 보기에서 회의록은 «카드»이고 정리 대기는 «줄»(안에 단추가 있다)이라, 섞으면 한 격자에
 * 카드와 줄이 번갈아 들어간다. ⇒ 그래서 화면은 **정리 대기를 위에 모아** 그린다.
 * ⇒ ⇒ ★ 그러면 **규칙과 화면이 어긋난다** — 시험은 「섞였나」를 재고 화면은 안 섞는다.
 *   ⇒ ⭐ 그래서 **규칙을 화면에 맞췄다**: 이 함수가 **그 순서 그대로** 내놓는다.
 *
 * ```
 * 정리 대기  started_at ?? created_at   내림차순   ← ★ 녹음을 «한» 시각이 사람이 기억하는 시각
 * 회의록     created_at                 내림차순
 * ```
 * ⚠ `started_at` 이 null 인 세션(시작 마커가 안 온 것)은 `created_at` 으로 떨어진다.
 * ★ 그리고 **먼저 두는 까닭**: 정리 대기는 **할 일이 있는 줄**이다(단추가 있다).
 *
 * ⭐⭐ 2026-10-20 (④) — 위 «먼저 모은다»는 ⛔ 더는 참이 아니다. 녹음 줄을 회의록 줄·카드와 같은 모양으로
 *   그리게 되어(사용자 결정) 두 덩이를 «한 축(날짜)»으로 섞는다 — `rowsForTab` 의 그 줄. 축은 그대로다
 *   (녹음 = started_at ?? created_at · 회의록 = created_at).
 */
export function rowSortKey(r: MergedRow): string {
  return r.at;
}

export function sessionSortAt(s: VoiceSessionItem): string {
  return s.started_at ?? s.created_at;
}

function byAtDesc(a: MergedRow, b: MergedRow): number {
  return rowSortKey(a) < rowSortKey(b) ? 1 : -1;
}

/**
 * 「정리 대기」 줄만 — 회의록이 **없는** 세션.
 *
 * ⛔ `minutes_id` 가 «있는» 세션은 넣지 않는다 — 넣으면 **같은 회의가 두 줄**로 보인다.
 */
export function pickAwaiting(sessions: readonly VoiceSessionItem[]): VoiceSessionItem[] {
  return sessions.filter(isAwaitingSummary);
}

/**
 * ★★★ 「**요약할 글이 있나**」 — ㉠ 의 판정 (㉤ 가 선 뒤 · 2026-10-05).
 *
 * ## ⛔ 왜 참/거짓이 «셋»인가 — 「모른다」를 「0」과 가른다
 *
 * ```
 * 'has'      transcript_chars > 0    ⇒ ★ 누르면 ⭐ 요약이 돈다
 * 'none'     transcript_chars === 0  ⇒ ⛔ 눌러도 ⭐ «안 산다» ⇒ 화면이 그 말을 한다
 * 'unknown'  그 칸이 ★ «아예 없다»   ⇒ ⚠ 옛 서버다 ⇒ ⛔ 「없습니다」라고 ★ 말하지 «않는다»
 * ```
 * ⇒ ⛔ `?? 0` 으로 뭉개면 **옛 서버에서 모든 줄이 「요약할 글이 없습니다」**가 된다 — 거짓말이다.
 *   ⇒ ★ 이 레포가 적어 둔 그 규율이다: **「모른다」와 「0」을 가른다.**
 *
 * ## ★ 왜 여기 «한 곳»인가
 *
 * 서버가 **세기만 하고 판정하지 않는다**(`voice.routes.ts:515` 의 그 줄) — 기준이 바뀌면 서버를
 * 또 고쳐야 하기 때문이다. ⇒ ★ 그래서 판정은 **화면 몫**이고, 화면 안에서 **두 벌이 되지 않게**
 * 이 함수 하나에 둔다(관제 지시 2026-10-05: 「`> 0` 을 한 곳에 두십시오」).
 *
 * ⚠ `transcript_chars` 는 **조각의 글자 수 합**이다 — 「요약이 몇 자 나올까」가 아니다.
 *   그리고 서버가 `btrim` 하므로 **공백만인 전사는 0** 이다(서버 SQL 의 그 줄).
 */
export type TranscriptTextState = 'has' | 'none' | 'unknown';

export function transcriptTextState(s: VoiceSessionItem): TranscriptTextState {
  // ⛔ `!s.transcript_chars` 로 쓰지 않는다 — 그러면 0 과 undefined 가 «같아진다».
  if (typeof s.transcript_chars !== 'number') return 'unknown';
  return s.transcript_chars > 0 ? 'has' : 'none';
}

/**
 * 「눌러도 안 사는 줄」인가 — ★ 'none' «하나»일 때만 참이다.
 *
 * ⇒ ⚠ `'unknown'` 은 **거짓**이다(막지 않는다) — 모르면 **종전처럼 누르게 둔다.**
 *   ⛔ 모른다고 막으면 옛 서버에서 **기능이 통째로 사라진다.**
 */
export function hasNothingToSummarize(s: VoiceSessionItem): boolean {
  return transcriptTextState(s) === 'none';
}

/**
 * ★★★ **「눌러도 «끝까지» 못 가는 줄」인가** — 전사가 실패한 조각이 하나라도 있나
 * (2026-10-07 사용자 결정 ⓑ).
 *
 * ## ⛔ 왜 — 서버의 물높이가 «그 자리에서» 멈춘다
 *
 * ```
 * chat-server/src/modules/voice/voice-summary.ts:90
 *   if (c.sttStatus !== 'done' && c.sttStatus !== 'device_side') break;
 * ```
 * `'failed'` 는 그 둘이 **아니므로** 거기서 `break` 다 ⇒ `summarized_count` 가 **영영 멈추고**
 * `maybeEnqueueFinalize` 가 안 불린다. ⇒ ★ 그래서 누르면 **「요약을 시작했습니다」만 뜨고
 * 끝이 안 난다** — 「눌렀는데 아무 일도 안 일어난다」의 두 번째 얼굴이다(㉠ 이 첫째였다).
 *
 * ## ⛔ 그리고 그 조각은 «되살릴 길이 없다»
 *
 * 되살리는 `UPDATE` 는 **한 곳**뿐이고 `WHERE … stt_status = 'skipped'` 다
 * (`voice.repo.ts:821` `reopenSkippedChunks`). 그 파일 `:772~790` 이 2026-09-28 정정으로
 * **「`failed` 는 «끝»이다 · 다시 하게 하려면 그 길을 새로 만들어야 한다 — 지금은 없다」**를
 * 적어 두었다(종전 주석 「다시 해 볼 수 있다」가 **거짓**이었고 앱을 잘못 이끌었다).
 * ⇒ ★ 그래서 이 갈래는 **말만 한다** — 「다시 전사」 같은 **없는 길을 내밀지 않는다.**
 * ⇒ ⚠ 0자 갈래도 이 줄에서는 내려받기를 주지 않는다(그 줄에 `audio_file_id` 가 **0건**이다) ⇒ 꼴이 같다.
 *
 * ## ⚠ 단추는 «끄지 않는다» — 0자 갈래와 같은 쪽이다
 *
 * 사용자 결정은 **ⓑ**(「누르게 두고 답한다」)다. ⓐ(단추 끔)는 **왜 꺼졌는지 안 보인다.**
 *
 * ## ★ 2×2 — 0자와 «겹칠» 때 어느 말이 나오나
 *
 * ```
 *                  실패 조각 0            실패 조각 > 0
 * 글자 있음        서버를 «부른다»        ⭐ 이 말 (요약이 끝까지 못 간다)
 * 글자 0           no_text 말             ⭐ no_text 말이 «이긴다»  ← 아래 까닭
 * ```
 * ⇒ ★ 0자 쪽이 이기는 까닭: 그때는 **서버를 애초에 안 부르므로 멈출 일이 없고**, 사용자의 문제는
 *   「글이 없다」다. 그 까닭(전사가 글자를 못 만들었다)은 `no_text_settled` 가 이미 말한다.
 *   ⇒ ⛔ 거기에 이 말을 더하면 **고칠 수 없는 두 소식**을 겹쳐 주는 것이 된다.
 * ⇒ ★ 그래서 `press()` 에서 **`hasNothingToSummarize` 를 먼저** 본다(순서가 규칙이다).
 *
 * ⚠ `failed_chunks` 는 **필수 칸**이라 「모른다」가 없다 — `transcript_chars` 와 달리 옛 서버
 *   걱정이 없다(`api/voice-sessions.ts` 의 선언을 보라: `failed_chunks: number`).
 */
export function summaryWouldStall(s: VoiceSessionItem): boolean {
  return s.failed_chunks > 0;
}

/**
 * ⭐⭐ 2026-10-20 (사용자 결정 · 시안 ②) — 실패 조각의 «처지». 서버가 녹음이 끝나면 실패 조각만 다시 받아 적고(1·10·60분),
 *   그래도 안 되는 조각만 빼고 요약한다(#2553). 화면은 서버 칸 `gave_up_chunks` 가 «있을 때만» 이 갈래를 쓴다.
 * ```
 * none      실패 조각 0                                  ⇒ 종전 그대로
 * retrying  다시 받아 적는 조각이 남았다(failed − gave_up > 0) ⇒ 서버를 «안» 부르고 「다시 받아 적고 있습니다」
 * skipped   실패 조각이 «전부» 포기됐다                      ⇒ 서버를 부른다 · 「n개는 빼고 요약합니다」
 * stall     옛 서버(칸 없음) — 서버가 안 건너뛴다              ⇒ 종전 「요약이 끝까지 가지 못합니다」(summaryWouldStall)
 * ```
 */
export type FailedChunkState = 'none' | 'retrying' | 'skipped' | 'stall';

export function failedChunkState(
  s: Pick<VoiceSessionItem, 'failed_chunks' | 'gave_up_chunks'>,
): FailedChunkState {
  if (s.failed_chunks <= 0) return 'none';
  if (typeof s.gave_up_chunks !== 'number') return 'stall';
  return s.failed_chunks - s.gave_up_chunks > 0 ? 'retrying' : 'skipped';
}

/** 아직 다시 받아 적는 실패 조각 수(칸이 없으면 0 — 옛 서버는 다시 받아 적지 않는다). */
export function retryingChunks(s: Pick<VoiceSessionItem, 'failed_chunks' | 'gave_up_chunks'>): number {
  return typeof s.gave_up_chunks === 'number' ? Math.max(0, s.failed_chunks - s.gave_up_chunks) : 0;
}

/**
 * ⭐⭐ 2026-10-20 (사용자 결정 · 시안 ③) — 「내부에 녹음된 내역이 없습니다.」를 말할 녹음인가.
 *   글자가 0 ∧ 전사가 «끝났다»(settled) ∧ 다시 받아 적는 조각이 없다. ⇒ 상세 「요약」 탭은 그 말 하나만 두고 단추를 안 둔다.
 * ⚠ 아직 올라오는 중 · 전사 중 · 다시 받아 적는 중이면 «아니다» — 곧 글이 생길 수 있다(종전 단추 · 말 그대로).
 * ⚠ 글자 칸이 «없으면»(옛 서버) 아니다 — 「모른다」는 「0」이 아니다(hasNothingToSummarize 와 같은 규율).
 */
export function voiceIsEmpty(s: VoiceSessionItem): boolean {
  return hasNothingToSummarize(s) && noTextReason(s) === 'settled' && failedChunkState(s) !== 'retrying';
}

/**
 * ⭐ 2026-10-20 (사용자 결정 「나」) — 회의록 상세의 「녹음된 내역 없음」. 회의록 응답에 실린 그 녹음의 숫자(`voice`)가 «있으면»
 *   그것으로 · 없으면(옛 서버 · 서버가 못 받음) 종전처럼 녹음 목록(100개)에서 찾은 줄로 ⇒ 목록 100개 «밖»의 녹음도 안다.
 * ⚠ 둘 다 있으면 회의록 응답의 숫자가 이긴다(앱 `minutesEmptyMinutesIds` 와 같은 차례).
 * ★ 판정은 «같은» `voiceIsEmpty` 다 — 숫자를 녹음 줄 꼴로 옮겨 한 벌로 잰다(판정에 안 쓰는 칸은 빈 값 · 앱도 같은 꼴이다).
 */
export function detailVoiceIsEmpty(voice: MinutesVoiceCounts | undefined, session: VoiceSessionItem | null): boolean {
  if (voice) {
    return voiceIsEmpty({
      session_id: '', title: '', created_at: '', started_at: null, ended_at: null, duration_ms: null,
      summarized_count: 0, audio_ready: false, audio_file_id: null, minutes_id: null, favorite: false,
      ...voice,
    });
  }
  return session !== null && voiceIsEmpty(session);
}

/**
 * ★★★ **글자가 0 일 때 ⇒ 「기다리면 되나, 안 되나」** (2026-10-06 · 관제 지적으로 갈라 적음).
 *
 * ## ⛔ 왜 갈라야 하나 — 한 문구로 덮으면 사용자가 못 가린다
 *
 * `transcript_chars === 0` 은 **두 가지**다:
 * ```
 * ⓐ 아직 전사가 ★ «안 끝났다»   ⇒ ⭐ 기다리면 글이 «생긴다»
 * ⓑ 전사는 끝났는데 ★ «빈 말»   ⇒ ⛔ 기다려도 «안 생긴다»(조용한 녹음 · STT 문턱)
 * ```
 * ⇒ ⛔ 처음 판은 둘 다에 「**녹음은 끝났지만** 전사된 글자가 없습니다」라고 말했다 —
 *   ⓐ 에서는 **안 잰 말**이다(관제가 코드 쓰기 전에 잡았다).
 *
 * ## ★ 무엇으로 가르나 — ⭐ 셋 중 «둘»은 가려진다
 *
 * ```
 * 서버가 주는 칸: chunk_count(선언) · received_chunks(온 것) ·
 *                transcribed_chunks(= stt done) · failed_chunks(= stt failed)
 * 말단 stt 상태 = done · failed · device_side
 *   ⇒ 근거: voice.repo.ts 의 「모든 조각이 **말단** stt 상태다」 머리말(2026-10-06 현재 :899)
 *     ＋ 「`device_side` 는 말단이다」 줄(:904) · 그 목록을 쓰는 SQL 은 `NOT IN ('done', 'failed',
 *     'device_side')`(:928) ⇒ ⚠ **줄 번호는 움직인다 — 그 «글자»로 찾을 것**
 * ⛔ 말단 아님  = pending · running · skipped    (skipped 는 reopenSkippedChunks 가 되살린다)
 * ```
 * ```
 * 'arriving'  ⓐ status === 'collecting'                ⇒ ✅ 녹음이 «도는 중»이다 ⇒ 더 온다
 *             ⓑ chunk_count 가 «수가 아니다»(null)      ⇒ ✅ 총수를 모른다 ⇒ 「끝났다」고 못 한다
 *             ⓒ received_chunks < chunk_count          ⇒ ✅ «확실히» 더 온다
 * 'settled'   다 왔고 ＋ 남은 조각이 0                   ⇒ ✅ «확실히» 다 끝났다 ⇒ 기다려도 안 된다
 * 'working'   다 왔지만 ★ 남은 조각이 있다               ⇒ ⚠ ⓐ 일 «수» 있다 ⇒ 기다려 보라고 한다
 * ```
 *
 * ## ⛔⛔ ⓐ·ⓑ 는 **둘 다** 있어야 한다 (2026-10-06 · 관제가 잡음)
 *
 * ```
 * 처음 판은 ⓒ «하나»였다 ⇒ ⛔ 로컬에 실제로 있는 두 줄이 ★ 「settled」로 떨어졌다:
 *   collecting · chunk_count NULL · received 1 · done 1 ⇒ 1 < null 은 ⭐ 1 < 0 ⇒ false
 *   ⇒ left = 1 − 1 − 0 = 0 ⇒ ★ 'settled' ⇒ ⛔ 「전사가 끝났지만 …」
 *   ⇒ ⇒ ★ 녹음이 «도는 중»인데 ⭐ 「끝났다」고 말했다 — 고치려던 그 거짓말이 그대로 남은 것이다
 * ```
 * ⇒ ★ 그리고 **한쪽만 막으면 다른 쪽이 샌다**(관제 지적 · 이 레포의 「가드는 제 방향만 닫는다」):
 * ```
 *   status ≠ collecting  ＋ chunk_count null  ⇒ ⓑ 가 막는다 (마이그로 넓힌 상태들에서 생길 수 있다)
 *   status = collecting  ＋ chunk_count 있음  ⇒ ⓐ 가 막는다 (COALESCE 가 그 꼴을 «허용»한다)
 * ```
 * ⇒ ⇒ ★ 그래서 **2×2 를 시험으로 그렸다**(`awaiting-summary.test.ts` 의 그 시험).
 * ## ✅ 2026-10-06 — **`pending_chunks`(㉥)가 서서 그 «좁음»이 없어졌다**
 *
 * ```
 * 종전:  left = received − done − failed   ⇒ ⛔ `device_side`(말단)가 «섞인다»
 *        ⇒ 말단인 조각을 ★ 「아직 도는 중」이라 말할 수 있었다
 * 지금:  pending_chunks  = pending ＋ running ＋ skipped 만         ⇒ ✅ 섞이지 않는다
 *        (서버 FILTER: stt_status NOT IN ('done','failed','device_side') · PR #2190)
 * ```
 *
 * ## ⚠ 옛 서버(그 칸이 «없는» 서버)는 — ⭐ **종전 셈으로 떨어진다**
 *
 * ⛔ 「모르니 무조건 `working`」으로 하지 **않았다.** 까닭을 재서 골랐다:
 * ```
 * ⓐ 무조건 working 이면 ⇒ ⛔ «정말로 끝난» 세션에도 ★ 「기다려 보라」고 한다 — 더 나쁜 거짓말이다
 * ⓑ 종전 셈은 ★ `device_side` 가 «있을 때만» 좁다 ⇒ ⭐ 그 밖에는 «정확하다»
 *    실측(2026-10-06 로컬): device_side ⭐ «0건» · 세션 21개 중 종전 셈과 새 칸이
 *    ★ «갈리는 줄 0» ⇒ 즉 오늘의 데이터에서 둘은 «같은 답»을 낸다
 * ⇒ ⇒ ★ 그래서 ⭐ 옛 서버에서는 «종전 셈»으로 떨어뜨리고, ★ 그 좁음을 여기 적어 둔다.
 * ```
 * ⚠ 그래도 `transcript_chars` 와 **다른 점**: 그쪽은 모르면 **막지 않는다**(기능이 사라지므로),
 *   이쪽은 **까닭 문구만** 고르는 자리라 **덜 해롭다** — 틀려도 「기다려 보라/끝났다」가 갈릴 뿐이다.
 *
 * ## ⚠⚠ 그 옛 서버 갈래가 **«언제» 쓰이나** — ⭐ 「몇 분짜리」다 (2026-10-06 · 관제 지적)
 *
 * ```
 * 웹과 서버는 ★ «함께» 배포된다 ⇒ ⭐ 이 갈래가 도는 것은 ★ «배포 창»(몇 분) 동안뿐이다
 * 그리고 28차가 ★ 이미 올라갔으므로 ⇒ ⭐ 지금 운영 서버에는 `pending_chunks` 가 ★ «있다»
 * ⇒ ⇒ ★ 그러니 위에 적은 「좁다」는 ⭐ «몇 분짜리 좁음»이다 — ⛔ «큰 결함이 아니다»
 * ⇒ ⇒ ⇒ ⛔ 그렇다고 ★ 지우면 «안 된다» — ⭐ 바로 그 창에서 쓰인다(그때 화면이 죽으면 안 된다)
 * ```
 * ⇒ ★ 「좁다」만 적어 두면 ⭐ 뒤에 누가 그것을 **큰 결함으로 읽는다** — 그래서 크기를 함께 적는다.
 *
 * ⚠ 로컬에서 `'working'` 갈래를 **실제로 본 적이 없다** — 조각이 전부 `done` 이다.
 *   ⇒ ★ `'arriving'` 은 **실제 두 줄**로 재지만(collecting 2건), `'working'` 은 ⭐ 소스로 낸 갈래다.
 */
export type NoTextReason = 'arriving' | 'working' | 'settled';

export function noTextReason(s: VoiceSessionItem): NoTextReason {
  /*
   * ⓐ 녹음이 «도는 중»이다.
   *
   * ⚠⚠ **2026-10-06 — 이 줄은 «둘째 겹»이 됐다.** 같은 날 사용자 결정으로 `isAwaitingSummary` 가
   *   `collecting` 을 **목록에서 먼저 막으므로**, 지금 이 함수의 **유일한 제품 호출자**
   *   (`AwaitingSummaryRow`)는 그런 세션을 **받지 않는다** ⇒ ★ 이 줄은 **도달 불가**다.
   *
   * ⇒ ⭐ **그래도 남긴다.** 까닭: 이 함수는 「목록에 넣나」가 아니라 **「그 세션을 «설명»하는」**
   *   함수다. `collecting` 이면 조각이 더 온다는 것은 **누가 묻든 참**이고, 지우면 다른 호출자가
   *   생기는 날 **조용히 틀린다**(「settled」라고 말한다).
   *
   * ⇒ ⛔ **언제 지워도 되나 — 아래 «둘 다»가 참일 때만**(하나만 보면 안 된다):
   * ```
   * ⓘ `isAwaitingSummary` 가 ★ 여전히 `collecting` 을 막는다      (2026-10-06 현재 ✅)
   * ⓙ `noTextReason` 의 제품 호출자가 ★ «그 목록을 거친 줄»뿐이다  (2026-10-06 현재 ✅ — 한 곳)
   * ```
   * ⇒ ★ 즉 **지금 지워도 «동작»은 같다.** 남기는 값은 ⭐ **ⓙ 가 깨지는 날의 보험**이다.
   *   ⇒ ⚠ 그러므로 뒤에 이 줄을 보고 「죽은 코드」라 할 사람에게: **맞다, 그리고 일부러다.**
   */
  if (s.status === 'collecting') return 'arriving';
  /*
   * ⓑ 총수를 «모른다» ⇒ ⛔ 「끝났다」고 말할 수 없다.
   *   ⚠ `=== null` 이 아니라 `typeof !== 'number'` 다 — 옛 서버가 그 칸을 **안 줄** 수도 있고,
   *     그때 `undefined < n` 은 **늘 false** 라 조용히 `settled` 로 샌다.
   */
  if (typeof s.chunk_count !== 'number') return 'arriving';
  // ⓒ 선언된 총수보다 «덜» 왔다.
  if (s.received_chunks < s.chunk_count) return 'arriving';
  /*
   * ⓓ ✅ **서버가 세어 준 수**(㉥) — ★ `device_side` 가 «섞이지 않는다».
   *   ⚠ `typeof` 로 본다 — 옛 서버는 이 칸을 **안 주고**, 그때 `undefined > 0` 은 **늘 false** 라
   *     조용히 `settled` 로 샌다(`chunk_count` 에서 겪은 그 길).
   */
  if (typeof s.pending_chunks === 'number') return s.pending_chunks > 0 ? 'working' : 'settled';
  /*
   * ⓔ ⚠ **옛 서버** — 종전 셈으로 떨어진다(머리말의 그 까닭).
   *   ⛔ `device_side` 가 있으면 **좁다**(말단인데 「도는 중」이라 말한다).
   *   ⚠ 한때 `Math.max(0, …)` 막이를 두었다가 **걷어냈다** — 사보타주로 지워도 전부 초록이라
   *     **효과가 0** 이었다(아래 `> 0` 이 음수를 이미 거른다). 효과 0 인 코드는 거짓 안심을 준다.
   */
  const left = s.received_chunks - s.transcribed_chunks - s.failed_chunks;
  return left > 0 ? 'working' : 'settled';
}

/**
 * 「정리 대기」 탭이 담는 **회의록** — ★ 판정은 여기 한 곳에 둔다.
 *
 * ⚠ `!== 'ready'` 이므로 `pending`(「대기」)와 `failed`(「실패」)가 **둘 다** 든다.
 *   그것이 2026-09-21 사용자 결정이고 앱의 `minutesMatchesTab` 과 **같은 가름**이다(위 머리말).
 *   ⇒ ⛔ `=== 'pending'` 으로 좁히면 **실패한 회의록이 어느 탭에도 안 보인다**(전체 말고).
 */
export function isAwaitingMinutes(m: Pick<MinutesListItem, 'status'>): boolean {
  return m.status !== 'ready';
}

/**
 * ★★★ **합치는 그 줄** — 탭이 무엇을 보여 줄지 고른다.
 *
 * ```
 * all       회의록 «전부»  ∪  정리 대기 세션 «전부»        ← ⭐ 기본
 * ready     회의록 중 status === 'ready'(라벨 「완료」) · ⛔ 정리 대기 세션은 안 들어간다
 * awaiting  정리 대기 세션 «전부»  ∪  ⭐ 회의록 중 status !== 'ready'   ← 2026-10-08 넓힘
 * ```
 * ⚠ **`awaiting` 에 회의록이 든다**(2026-10-08) — 그전에는 통째로 뺐다. 까닭은 머리말에 있다.
 * ⚠ `ready` 가 「완료」인 근거는 i18n 값이다 — `minutes.status.ready` = 「완료」
 *   (`minutes-i18n.ts:168`). ⇒ ★ 열쇠 이름이 아니라 **값**으로 맞췄다.
 */
export function rowsForTab(
  tab: MinutesTab,
  minutes: readonly MinutesListItem[],
  sessions: readonly VoiceSessionItem[],
): MergedRow[] {
  const awaiting: MergedRow[] = tab === 'ready'
    ? []
    : pickAwaiting(sessions).map((s) => ({ kind: 'awaiting', at: sessionSortAt(s), session: s }));
  // ⭐ 2026-10-08 — awaiting 에서도 회의록을 «뺀지 않는다». 요약이 안 된 것만 고른다.
  const mins: MergedRow[] = minutes
    .filter((m) => minutesInTab(tab, m))
    .map((m) => ({ kind: 'minutes', at: m.created_at, minutes: m }));
  /*
   * ⭐⭐ 2026-10-20 (④ · 사용자 결정 「없애고 날짜순」) — 두 덩이를 «하나로» 날짜순(새것부터)에 섞는다.
   *   사용자 원문: 「웹에서만 전체, 정리대기 탭에서 보면 상단에 정리대기로 표시된다. 다른 목록과 동일하게
   *   디자인하고 대기로 표시해달라.」 ⇒ 녹음 줄도 회의록 줄과 같은 모양(AwaitingSummaryRow)이고, 카드
   *   보기에서도 카드로 그린다 ⇒ 위 정렬 머리말의 «섞을 수 없다»(카드와 줄)가 더는 참이 아니다.
   *   ⇒ 그래서 10-10 웹 ㉣ 의 두 묶음 머리글도 걷었다(MergedCollection).
   * ⚠ 종전 줄(각각 정렬해 이어 붙이기) — `[...awaiting.sort(byAtDesc), ...mins.sort(byAtDesc)]`.
   */
  return [...awaiting, ...mins].sort(byAtDesc);
}

/**
 * 미리보기 — ⛔ **「정리 대기」는 자르지 않는다.**
 *
 * ## ★ 왜 한쪽만 자르나
 *
 * 「최근」 섹션은 회의록을 `RECENT_PREVIEW` 개만 보이고 나머지는 **「전체 보기」**(서버 페이징)로
 * 넘긴다. ⇒ ⛔ 그런데 「전체 보기」 화면은 **회의록만** 그린다(그 축이 minutes-svc 의 offset 이다).
 * ⇒ ⇒ ★ 그래서 정리 대기를 **여기서 자르면 잘린 것을 볼 자리가 «아무 데도 없다»** —
 *   사용자는 「내 녹음이 어디 갔나」를 묻는다. ⇒ ⭐ **정리 대기는 늘 전부 보인다.**
 * ⚠ 그 대가: 정리 대기가 많으면 이 섹션이 길어진다. 서버 상한이 100 이라 **그만큼이 상한**이다.
 */
export function previewRows(rows: readonly MergedRow[], minutesLimit: number): MergedRow[] {
  let kept = 0;
  const out: MergedRow[] = [];
  for (const r of rows) {
    if (r.kind === 'minutes') {
      if (kept >= minutesLimit) continue;
      kept++;
    }
    out.push(r);
  }
  return out;
}

/**
 * 탭 머리에 붙는 수.
 *
 * ⛔ **「17」·「16」 같은 수를 글자로 적지 않는다** — 수가 움직인다(2026-10-02 엔 16,
 * 10-04 엔 17이었다). ⇒ ★ 늘 **지금 받은 목록**에서 센다.
 *
 * ## ⛔⛔ 겹셈 함정 — `all` 에 `awaiting` 을 그대로 쓰면 **두 번 센다**
 *
 * `all` 은 `minutes.length + <정리 대기 «세션»>` 이다. 그 `minutes.length` 안에
 * **요약이 안 된 회의록이 이미 들어 있다**. ⇒ ★ 그러므로 2026-10-08 에 넓힌
 * `awaiting`(= 세션 ＋ 그 회의록)을 `all` 에 쓰면 **그 회의록을 두 번 센다.**
 * ⇒ ⇒ ★ 그래서 변수를 **둘로** 두고 **이름에 가름이 보이게** 했다:
 * `awaitingSessions`(`all` 이 쓰는 것) · `awaitingTab`(「정리 대기」 탭이 쓰는 것).
 * ⇒ ⛔ 한 이름으로 합치면 다음 사람이 **또 겹센다**.
 */
export function tabCounts(
  minutes: readonly MinutesListItem[],
  sessions: readonly VoiceSessionItem[],
): Record<MinutesTab, number> {
  /*
   * ⭐ 2026-10-10 (웹 ㉣) — 탭 수는 «묶음 수의 합»이다. 목록 머리글(「정리 대기 N」·「회의록 N」)과
   *   «같은 함수»(groupCounts)에서 나온다 ⇒ 탭 수와 머리글 수가 갈릴 수 없다.
   *   ⇒ 겹셈 함정(위 머리말)도 그 함수가 막는다: 정리 대기 묶음은 «회의록 행이 없는» 세션뿐이라
   *     회의록 묶음과 겹치지 않는다.
   */
  const sum = (t: MinutesTab): number => {
    const g = groupCounts(t, minutes, sessions);
    return g.awaiting + g.minutes;
  };
  return { all: sum('all'), ready: sum('ready'), awaiting: sum('awaiting') };
}

/**
 * ⭐ 2026-10-10 (웹 ㉣ · 사용자 결정 「권고」) — 그 탭에서 회의록 묶음에 «드는» 회의록인가. 거르개 «한 곳».
 * ```
 * all       전부
 * ready     status === 'ready'
 * awaiting  status !== 'ready'(isAwaitingMinutes — 실패한 회의록도 든다)
 * ```
 */
export function minutesInTab(tab: MinutesTab, m: Pick<MinutesListItem, 'status'>): boolean {
  if (tab === 'ready') return m.status === 'ready';
  if (tab === 'awaiting') return isAwaitingMinutes(m);
  return true;
}

/**
 * ⭐ 2026-10-10 (웹 ㉣) — 목록 «묶음 머리글»의 수: 「정리 대기 N」(녹음 줄) · 「회의록 N」.
 *
 * ⭐ 2026-10-20 (④) — 두 머리글은 걷었다(한 목록 날짜순 · `rowsForTab`). 이 함수는 ★ 탭 수(`tabCounts`)의
 *   «한 출처»로 남는다 — 아래 겹셈·범위 머리말은 그 수에 그대로 걸린다.
 *
 * ## 왜 — 「회의록이 안 보인다」(2026-10-09 운영)
 *   목록은 정리 대기를 «위에 모아» 그리고 회의록을 그 «아래»에 그린다(rowSortKey 머리말 — 카드와 줄을 섞을
 *   수 없어서). 정리 대기가 길면 회의록이 화면 밖으로 밀려 «없는 것처럼» 보였다. ⇒ 사용자 결정 ㉣:
 *   차례는 그대로 두고 두 묶음에 머리글과 수를 단다 — 회의록이 아래 있어도 「다른 쪽이 있다」가 보인다.
 *
 * ## ⚠ 수는 «미리보기 전» 묶음 전체다
 *   화면은 회의록을 RECENT_PREVIEW 만큼만 그린다(previewRows) — 머리글은 «보이는 수»가 아니라 «묶음 수»다.
 *
 * ## ⚠⚠ 정확한 범위 — 회의록 ≤100 · 녹음 ≤100 에서만 정확하다
 *   «지금 받은 목록»에서 센다(listMinutes limit 100 · listVoiceSessions limit 100). 넘으면 탭 수와 «함께»
 *   모자란다 — 지금 탭의 한계 그대로이고 새로 생긴 것이 아니다. ⛔ 서버 수로 «한쪽만» 바꾸지 말 것 —
 *   탭 수와 머리글 수가 갈린다(tabCounts 가 이 함수를 부르는 것이 그 가드다).
 *   ⏳ 만료: `GET /voice/sessions` 응답이 `total` 을 내놓기 시작하면 ⇒ 이 화면 셈을 그 수로 바꾸고
 *     탭 수도 함께 옮긴다(둘이 갈리면 안 된다). 트리거 = `listVoiceSessions` 의 반환 타입에 `total` 이
 *     들어오는 날(지금은 items · nextCursor · stt_available · summary_available 뿐 — 2026-10-10 실측).
 */
export function groupCounts(
  tab: MinutesTab,
  minutes: readonly MinutesListItem[],
  sessions: readonly VoiceSessionItem[],
): { awaiting: number; minutes: number } {
  return {
    // ★ 「완료」 탭에는 정리 대기 줄이 안 든다(rowsForTab 과 같은 규칙).
    awaiting: tab === 'ready' ? 0 : pickAwaiting(sessions).length,
    minutes: minutes.filter((m) => minutesInTab(tab, m)).length,
  };
}
